/// <reference lib="dom" />
/**
 * webWeaponPickupDraw.ts
 * Canvas2D draw helper for weapon pickups on the world canvas.
 *
 * Prefers a per-kind sprite from {@link RenderAssets}; falls back to a
 * greybox square (per-kind fill + 2px top-edge highlight) while the
 * asset bundle hasn't loaded yet. Adds a small vertical bob so pickups
 * read as "collectable" instead of static decor.
 *
 * @module webWeaponPickupDraw
 */

import { gameState } from '../engine/gameState';
import { TILE_SIZE, VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import { WEAPON_PICKUP_COLORS, WEAPON_PICKUP_OUTLINE_COLOR } from '../../constants/rendering';
import { getRenderAssets } from './renderAssetStore';
import type { RenderAssets } from './RenderAssets';
import type { WeaponPickupKind } from '../engine/gameStateTypes';

/** Bob amplitude in px (matches the seeds/shards bob in webCollectibleDraw). */
const BOB_AMP_PX = 2;
/** Bob period — same 400ms divisor as collectibles for visual consistency. */
const BOB_DIVISOR_MS = 400;

/**
 * Resolve the per-kind sprite from the active asset bundle. Returns
 * `null` when assets haven't loaded yet so the caller falls back to
 * the colour rect.
 *
 * @param kind   - Weapon-pickup kind from the runtime entry.
 * @param assets - Active asset bundle (may be `null` pre-load).
 * @returns Decoded image for that kind, or `null` if unavailable.
 */
function spriteForKind(
  kind: WeaponPickupKind,
  assets: RenderAssets | null,
): HTMLImageElement | null {
  if (assets === null) return null;
  switch (kind) {
    case 'gem_shard': return assets.weaponPickupGemShardImg;
    case 'boomerang': return assets.weaponPickupBoomerangImg;
    case 'fire_orb': return assets.weaponPickupFireOrbImg;
    case 'ice_crystal': return assets.weaponPickupIceCrystalImg;
  }
}

/**
 * Draw uncollected weapon pickups from `gameState.weaponPickups`.
 *
 * Off-screen pickups are skipped via a tile-sized AABB cull. The bob is a
 * deterministic sine of `timeMs` so two pickups at the same time render at
 * the same offset (no per-pickup phase needed at this greybox stage).
 *
 * @param ctx    - Canvas 2D rendering context.
 * @param camX   - Camera X offset in world space.
 * @param camY   - Camera Y offset in world space.
 * @param timeMs - Current timestamp in ms (drives the bob animation).
 */
export function drawWeaponPickups(
  ctx: CanvasRenderingContext2D,
  camX: number,
  camY: number,
  timeMs: number,
): void {
  const { weaponPickups } = gameState;
  // DECISION: Resolve assets once per frame, not per pickup — the
  // module-singleton lookup is cheap but the per-frame contract is
  // "all pickups share the same bundle snapshot".
  const assets = getRenderAssets();
  // DECISION: Single sin() per frame, not per pickup — bob is intentionally
  // in-phase across all pickups (matches the collectibles renderer).
  const bobOffset = Math.sin(timeMs / BOB_DIVISOR_MS) * BOB_AMP_PX;

  for (let i = 0; i < weaponPickups.length; i++) {
    const p = weaponPickups[i];
    if (p.collected) continue;

    const dx = Math.round(p.x - camX);
    const dy = Math.round(p.y - camY + bobOffset);

    // AABB viewport cull — skip if fully off-screen.
    if (dx + TILE_SIZE < 0 || dx > VIEWPORT_WIDTH) continue;
    if (dy + TILE_SIZE < 0 || dy > VIEWPORT_HEIGHT) continue;

    const sprite = spriteForKind(p.kind, assets);
    if (sprite !== null) {
      ctx.drawImage(sprite, dx, dy, TILE_SIZE, TILE_SIZE);
    } else {
      ctx.fillStyle = WEAPON_PICKUP_COLORS[p.kind];
      ctx.fillRect(dx, dy, TILE_SIZE, TILE_SIZE);
      ctx.fillStyle = WEAPON_PICKUP_OUTLINE_COLOR;
      ctx.fillRect(dx, dy, TILE_SIZE, 2);
    }
  }
}
